import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  HostListener,
  inject,
  input,
  signal,
} from '@angular/core';
import { MapComponent, MarkerComponent } from '@maplibre/ngx-maplibre-gl';
import { MAPTILES_KEY } from '@wheere-front/shared-map-libre';
import {
  ChannelKey,
  DialogComponent,
  MapAntennaMarkerComponent,
  WhButtonComponent,
  CHANNEL_PALETTE,
  DEFAULT_CHANNEL_HEX,
  normalizeSource,
} from '@wheere-front/shared-ui';
import { calculateDynamicPadding, chartCustomColors, getFitBounds } from '@wheere-front/shared-util';
import { Gateway } from '@wheere/api-site';
import { IntegratorTracker, Position } from '@wheere/api-tracker';
import { Map as MapLibreMap } from 'maplibre-gl';
import { TranslocoModule } from '@jsverse/transloco';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { clampToNow } from '../util/channel-presence';
import { MapStateService } from '../util/map-state.service';
import { TrackersV7Store } from '../data/trackers-v7.store';

dayjs.extend(relativeTime);

interface SourceConfig {
  key: string;
  name: string;
  color: string;
}

/** Legend entries, colours from the shared palette; DEFAULT is the "no channel selected" entry. */
const SOURCE_CONFIGS: Record<string, SourceConfig> = {
  ...Object.fromEntries(Object.values(CHANNEL_PALETTE).map(c => [c.key, { key: c.key, name: c.name, color: c.hex }])),
  DEFAULT: { key: 'DEFAULT', name: 'Default', color: DEFAULT_CHANNEL_HEX },
};

/**
 * V8 integrator live tracker map — fullscreen modal that mirrors the inside admin's
 * {@code TrackerMapPositionsComponent} but stripped of supervised log, building level,
 * custom points, timed logging and distance-measure features (per integrator persona,
 * none of those apply to the operator role).
 *
 * <p>What it shows (maquette integrator-refonte, 2026-10-08):
 * <ul>
 *   <li>The current tracker position as a coloured ping (colour follows the active source:
 *       4G/ALGO/BLE/LORA/DEFAULT).</li>
 *   <li>Multi-source: when {@link Tracker#channelSelection} lists more than one channel,
 *       one marker per source; the legend toggles each source on/off.</li>
 *   <li>Antenna markers (no pseudo-range circles any more).</li>
 *   <li>A compact label top-left (name · freshness · channel), a minimal legend bottom-left
 *       and the actions top-right (map style, center, close). Same layout on mobile: the
 *       label drops below the action row; the former bottom sheet is gone.</li>
 * </ul>
 *
 * <p>Closing the dialog: the top-right Close button or {@code Esc}. The {@link HostListener}
 * watches keydown.escape on the document so the operator doesn't have to hit any
 * specific element first.
 *
 * <p>Position source: reads {@link TrackersV7Store#lastPosition} which is fed by the
 * page-level WebSocket subscription on {@code /topic/tracker/{id}/position}. We
 * accumulate positions by source locally to support multi-source rendering — the
 * store only carries the latest fused position.
 */
@Component({
  selector: 'lib-tracker-live-map',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MapComponent, MarkerComponent, MapAntennaMarkerComponent, WhButtonComponent, TranslocoModule],
  // Local MapStateService instance — the satellite toggle, fly-to and
  // map ref are scoped to this dialog. Without this provider override the dialog would
  // share state with another map open on a different page (replay map, etc.).
  providers: [MapStateService],
  template: `
    <div class="relative h-screen w-screen bg-white dark:bg-slate-900">
      <!-- Étiquette compacte (haut gauche) : nom · fraîcheur · canal. Remplace l'ancien panneau
           latéral ; sur mobile elle passe sous la rangée d'actions pour ne pas la chevaucher. -->
      <div
        data-testid="live-map-label"
        class="absolute left-3 top-14 z-20 flex max-w-[calc(100%-1.5rem)] items-center gap-2 rounded-lg border border-gray-200 bg-white/95 px-3 py-1.5 text-sm shadow-sm md:top-3 md:max-w-[45%] dark:border-slate-700 dark:bg-slate-800/95">
        <span class="size-2 shrink-0 rounded-full" [class]="currentDotClass()"></span>
        <span class="truncate font-bold text-gray-900 dark:text-slate-100">{{ trackerName() }}</span>
        @if (isPositionValid()) {
          @if (positionAgo(); as ago) {
            <span class="shrink-0 text-gray-500 dark:text-slate-400">· {{ ago }}</span>
          }
          @if (channelLabel(); as channel) {
            <span class="shrink-0 text-gray-500 dark:text-slate-400">· {{ channel }}</span>
          }
        } @else {
          <span class="shrink-0 text-gray-500 dark:text-slate-400"
            >· {{ 'trackerLiveMap.noPositionYet' | transloco }}</span
          >
        }
      </div>

      <!-- Actions (haut droite) : style de carte, centrer, fermer. -->
      <div data-testid="live-map-actions" class="absolute right-3 top-3 z-30 flex items-center gap-2">
        <wh-button variant="outline" size="xs" userClass="shadow-sm" (click)="onToggleMapStyle()">
          {{ (mapStyleType() === 'street' ? 'trackerLiveMap.satellite' : 'trackerLiveMap.street') | transloco }}
        </wh-button>
        <wh-button
          variant="outline"
          size="xs"
          userClass="shadow-sm"
          [disabled]="!isPositionValid()"
          [title]="'trackerLiveMap.flyToCurrent' | transloco"
          (click)="flyToPosition()">
          {{ 'trackerLiveMap.center' | transloco }}
        </wh-button>
        <wh-button
          variant="outline"
          size="xs"
          userClass="gap-1 shadow-sm"
          [dialogCloseFor]="dialog()"
          [title]="'trackerLiveMap.closeTitle' | transloco">
          {{ 'trackerLiveMap.close' | transloco }}
          <!-- iconoir xmark -->
          <svg
            class="size-3.5"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.5"
            aria-hidden="true">
            <path
              d="M6.758 17.243 12.001 12m5.243-5.243L12 12m0 0L6.758 6.757M12.001 12l5.243 5.243"
              stroke-linecap="round"
              stroke-linejoin="round" />
          </svg>
        </wh-button>
      </div>

      <!-- Légende minimale (bas gauche). En multi-canal, chaque canal est un bouton qui
           l'affiche ou le masque sur la carte. -->
      <div
        data-testid="live-map-legend"
        class="absolute bottom-4 left-3 z-20 flex max-w-[calc(100%-1.5rem)] flex-wrap items-center gap-x-3 gap-y-1.5 rounded-lg border border-gray-200 bg-white/95 px-3 py-2 text-xs text-gray-600 shadow-sm dark:border-slate-700 dark:bg-slate-800/95 dark:text-slate-300">
        @if (isMultiSourceMode()) {
          @for (src of availableSources(); track src) {
            <button
              type="button"
              class="inline-flex items-center gap-1.5 rounded px-1 py-0.5 hover:bg-gray-100 dark:hover:bg-slate-700"
              [class.opacity-40]="!isSourceEnabled(src)"
              [attr.aria-pressed]="isSourceEnabled(src)"
              [title]="(isSourceEnabled(src) ? 'trackerLiveMap.hideOnMap' : 'trackerLiveMap.showOnMap') | transloco"
              (click)="toggleSource(src)">
              <span class="size-2 rounded-full" [class]="dotClass(src)"></span>
              {{ channelName(src) }}
            </button>
          }
        } @else {
          <span class="inline-flex items-center gap-1.5">
            <span class="size-2 rounded-full" [class]="currentDotClass()"></span>
            {{ 'trackerLiveMap.legendTracker' | transloco }}
          </span>
        }
        @if (flatAntennas().length > 0) {
          <span class="inline-flex items-center gap-1.5">
            <span class="size-2 rounded-sm bg-gray-500 dark:bg-slate-400"></span>
            {{ 'trackerLiveMap.legendAntenna' | transloco }}
          </span>
        }
      </div>

      <!-- ────────────────────────────────────────────────────────
        MAP
        ──────────────────────────────────────────────────────── -->
      @defer {
        <mgl-map
          [mapStyle]="mapStyle()"
          [zoom]="[18]"
          [fitBounds]="fitBounds()"
          [fitBoundsOptions]="fitBoundsOptions()"
          [maxZoom]="21"
          (mapLoad)="onMapLoad($event)"
          class="h-full w-full">
          <!-- Single-source mode: one animated marker at the current position.
               The ping span is keyed on the position OBJECT (each WS frame is a fresh
               reference, even when the coords are identical) so it re-runs its one-shot
               animation per received frame — visible feedback that the position is
               refreshing even when the tracker doesn't move. -->
          @if (!isMultiSourceMode() && isPositionValid()) {
            <mgl-marker [lngLat]="trackerLngLat()!">
              <span class="relative flex size-3.5">
                @for (p of [position()!]; track p) {
                  <span
                    class="animate-ping-once absolute inline-flex h-full w-full rounded-full opacity-75"
                    [style.background-color]="currentMarkerColor() + '80'"></span>
                }
                <span
                  class="relative inline-flex size-3.5 rounded-full"
                  [style.background-color]="currentMarkerColor()"></span>
              </span>
            </mgl-marker>
          }

          <!-- Multi-source mode: one marker per enabled source. Same per-frame one-shot
               ping, keyed on that source's own position object. -->
          @if (isMultiSourceMode()) {
            @for (src of availableSources(); track src) {
              @let pos = positionsBySource().get(src);
              @if (isSourceEnabled(src) && pos && isValid(pos)) {
                <mgl-marker [lngLat]="[pos.longitude!, pos.latitude!]">
                  <span class="relative flex size-3.5">
                    @for (p of [pos]; track p) {
                      <span
                        class="animate-ping-once absolute inline-flex h-full w-full rounded-full opacity-75"
                        [style.background-color]="getSourceConfig(src).color + '80'"></span>
                    }
                    <span
                      class="relative inline-flex size-3.5 rounded-full"
                      [style.background-color]="getSourceConfig(src).color"></span>
                  </span>
                </mgl-marker>
              }
            }
          }

          <!-- V8 unified topology — reception points are antennas everywhere. Flatten
               every gateway's antennas in (orderSite × orderGateway) order and render
               one marker per antenna. The flat index drives pseudo[i] / powerGw[i],
               same indexing as the MQTT city/v3/{slug}/gw/{i} topics. Each popup names
               the owning gateway so the operator can match the dot to a row. -->
          @for (a of flatAntennas(); track a.id; let i = $index) {
            <lib-map-antenna-marker
              [antenna]="a.antenna"
              [color]="palette[i % palette.length]"
              [gatewayName]="a.gatewayName"
              [gwPower]="position()?.powerGw ? position()?.powerGw?.[i] : 0"></lib-map-antenna-marker>
          }
        </mgl-map>
      }
    </div>
  `,
})
export class TrackerLiveMapComponent {
  // ── Required inputs
  dialog = input.required<DialogComponent>();
  trackerId = input.required<string>();
  // ── Optional inputs
  tracker = input<IntegratorTracker | null>(null);
  gateways = input<Gateway[] | undefined>(undefined);

  protected readonly palette = chartCustomColors;

  /** Read the current position from the store — fed by the page-level WS subscription. */
  #store = inject(TrackersV7Store);
  protected readonly position = this.#store.lastPosition;

  /** Map style state — provided locally so this component owns its own MapStateService
   * instance and doesn't fight with another map elsewhere in the app. */
  #mapState = inject(MapStateService);
  protected readonly mapStyle = this.#mapState.mapStyle;
  protected readonly mapStyleType = this.#mapState.mapStyleType;

  /** Horloge de l'étiquette : « il y a X » se rafraîchit sans attendre une nouvelle position. */
  readonly #now = signal(Date.now());

  /** Accumulated positions by source — kept locally because the store only carries the
   * latest fused position. Updated whenever {@code #store.lastPosition} changes via the
   * effect in the constructor. */
  protected readonly positionsBySource = signal<Map<string, Position>>(new Map());

  /** Sources the user has enabled in multi-source mode (initialised from the tracker's
   * {@code channelSelection} when it lands). */
  protected readonly enabledSources = signal<Set<string>>(new Set());

  protected readonly availableSources = computed<string[]>(() => {
    const channels = this.tracker()?.channelSelection?.selectedChannels;
    if (!channels?.length) return [];
    return [...channels].map(c => normalizeSource(c)).filter(c => c !== 'DEFAULT');
  });

  protected readonly isMultiSourceMode = computed(() => this.availableSources().length > 1);

  /** Resolved source for the single-marker mode — the channel selected explicitly, or
   * the source field on the latest position frame. */
  protected readonly displaySource = computed<string | null>(() => {
    const sources = this.availableSources();
    if (sources.length === 1) return sources[0];
    const pos = this.position();
    return pos?.source ? normalizeSource(pos.source) : null;
  });

  protected readonly trackerName = computed(() => this.tracker()?.name || this.tracker()?.deviceId || this.trackerId());

  /** Canal de la position affichée, libellé court de la palette (4G, ALGO, BLE, LoRa). */
  protected readonly channelLabel = computed<string | null>(() => {
    const src = this.displaySource();
    return src ? (CHANNEL_PALETTE[src as ChannelKey]?.label ?? null) : null;
  });

  /** Fraîcheur de la position (« il y a 5 minutes »), bornée à maintenant contre la dérive d'horloge. */
  protected readonly positionAgo = computed<string | null>(() => {
    const ts = this.position()?.timestamp;
    if (!ts) return null;
    const now = new Date(this.#now());
    return dayjs(clampToNow(new Date(ts), now)).from(now);
  });

  protected readonly currentDotClass = computed(() => this.dotClass(this.displaySource()));

  protected readonly currentMarkerColor = computed<string>(() => {
    const src = this.displaySource();
    if (src && SOURCE_CONFIGS[src]) return SOURCE_CONFIGS[src].color;
    return SOURCE_CONFIGS['DEFAULT'].color;
  });

  protected readonly isPositionValid = computed<boolean>(() => {
    const p = this.position();
    return (
      !!p &&
      typeof p.latitude === 'number' &&
      typeof p.longitude === 'number' &&
      !isNaN(p.latitude) &&
      !isNaN(p.longitude)
    );
  });

  protected readonly trackerLngLat = computed<[number, number] | null>(() => {
    const p = this.position();
    if (!this.isPositionValid() || !p) return null;
    return [p.longitude!, p.latitude!];
  });

  /**
   * Flat antenna union across every gateway, ordered by gateway.orderSite then
   * antenna.orderGateway — same flat reception-point order the firmware uses for
   * MQTT topics and tracker {@code sync[i]} arrays, so the {@code i} we hand to
   * pseudo[i]/powerGw[i] in the template lines up with what the device publishes.
   * Each entry carries its owning gateway name for the popup header (#16).
   */
  protected readonly flatAntennas = computed<
    { id: string; antenna: NonNullable<Gateway['antennas']>[number]; gatewayName: string | undefined }[]
  >(() => {
    const gws = this.gateways();
    if (!gws?.length) return [];
    const out: { id: string; antenna: NonNullable<Gateway['antennas']>[number]; gatewayName: string | undefined }[] =
      [];
    const sortedGws = [...gws].sort((a, b) => (a.orderSite ?? 0) - (b.orderSite ?? 0));
    for (const g of sortedGws) {
      const antennas = [...(g.antennas ?? [])].sort((x, y) => (x.orderGateway ?? 0) - (y.orderGateway ?? 0));
      for (const a of antennas) {
        // Skip antennas without coordinates — they can't be plotted and would also
        // throw off the pseudo/power index for everything after them. Use the row
        // id as the stable @for tracker.
        if (a.longitude == null || a.latitude == null) continue;
        out.push({ id: a.id ?? `${g.id}-${a.orderGateway}`, antenna: a, gatewayName: g.name ?? undefined });
      }
    }
    return out;
  });

  /** Initial fit-bounds from the flat antenna union. Falls back to undefined when no
   * antenna has coordinates yet — mgl-map renders centered with zoom 18. */
  protected readonly fitBounds = computed<[[number, number], [number, number]] | undefined>(() => {
    const coords = this.flatAntennas().map(a => ({ lng: a.antenna.longitude!, lat: a.antenna.latitude! }));
    if (coords.length > 0) return getFitBounds(coords);
    return undefined;
  });

  protected readonly fitBoundsOptions = computed(() => ({
    padding: this.fitBounds() ? calculateDynamicPadding(this.fitBounds()!) : 20,
    animate: false,
  }));

  constructor() {
    // Whenever the tracker's channel selection lands, enable every source by default
    // so the user sees them all and can toggle off the ones they don't care about.
    effect(
      () => {
        const sources = this.availableSources();
        if (sources.length > 0) {
          this.enabledSources.set(new Set(sources));
        }
      },
      { allowSignalWrites: true }
    );

    // Accumulate positions by source as new frames come in via the WS sub. Without
    // this, multi-source mode would only ever show one marker at a time (the latest
    // overall fix), defeating the purpose.
    effect(
      () => {
        const pos = this.position();
        if (!pos?.source) return;
        const key = normalizeSource(pos.source);
        this.positionsBySource.update(m => {
          const next = new Map(m);
          next.set(key, pos);
          return next;
        });
      },
      { allowSignalWrites: true }
    );

    const clock = setInterval(() => this.#now.set(Date.now()), 30_000);
    inject(DestroyRef).onDestroy(() => clearInterval(clock));

    // Touch the MAPTILES_KEY token so the bundler keeps it in the providers chain even
    // when the consuming app is built with --aot tree-shaking.
    inject(MAPTILES_KEY, { optional: true });
  }

  /** Esc closes the dialog. {@link HostListener} on the document so the user doesn't
   * need to tab into a focusable element first. */
  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.dialog().hide();
  }

  protected onMapLoad(map: MapLibreMap): void {
    this.#mapState.map.set(map);
  }

  protected onToggleMapStyle(): void {
    this.#mapState.onToggleMapStyle();
  }

  protected flyToPosition(): void {
    const lngLat = this.trackerLngLat();
    if (!lngLat) return;
    this.#mapState.flyTo(lngLat[0], lngLat[1]);
  }

  protected isSourceEnabled(source: string): boolean {
    return this.enabledSources().has(source);
  }

  protected toggleSource(source: string): void {
    this.enabledSources.update(s => {
      const next = new Set(s);
      if (next.has(source)) next.delete(source);
      else next.add(source);
      return next;
    });
  }

  /** Pastille de couleur d'un canal, par classe (porte sa variante sombre) plutôt qu'en dur. */
  protected dotClass(source: string | null): string {
    return (source && CHANNEL_PALETTE[source as ChannelKey]?.marker) || 'bg-primary-500';
  }

  protected channelName(source: string): string {
    return CHANNEL_PALETTE[source as ChannelKey]?.label ?? source;
  }

  protected getSourceConfig(source: string): SourceConfig {
    return SOURCE_CONFIGS[source] ?? SOURCE_CONFIGS['DEFAULT'];
  }

  protected isValid(p: Position | undefined): boolean {
    return (
      !!p &&
      typeof p.latitude === 'number' &&
      typeof p.longitude === 'number' &&
      !isNaN(p.latitude) &&
      !isNaN(p.longitude)
    );
  }
}
